// apps/client/src/components/clients/ClientCareWhoToCallPanel.tsx
// Two-column "Who To Call" panel for the Client Care page.
// LEFT = Overdue (amber→red) · RIGHT = Due this week (emerald→blue).
// Data is RBAC-scoped server-side by listDueClientEvents (broker → own clients,
// assistant/analyst → linked brokers, office admin → offices, super_admin → all).
// A row click opens the existing ClientDetailDrawer via onSelectClient.

import React, { useState } from 'react';
import {
  PhoneCall, Phone, Mail, AlertTriangle, Calendar, Check, ChevronDown, Info,
  PartyPopper, Cake, Repeat, Percent, CalendarClock, Hourglass, RefreshCcwDot,
  ClipboardCheck, BellRing, ListChecks,
} from 'lucide-react';
import { useDueClientEvents, type DueClientEventRow } from '../../hooks/useDueClientEvents';

interface ClientCareWhoToCallPanelProps {
  companyId?: string;
  /** Opens the client drawer the page already owns (sets selectedClientId). */
  onSelectClient: (clientId: string) => void;
  /** Attribute filters from the page, applied server-side so the panel matches
   *  the table/cards. Overdue is the panel's native dimension (not filtered here). */
  officeId?: string | null;
  brokerDisplay?: string | null;
  loanBookEntityCode?: string | null;
  status?: 'active' | 'inactive' | null;
}

const REASON_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  settlement_anniversary: PartyPopper,
  birthday: Cake,
  periodic_review: Repeat,
  bid_annual: Repeat,
  post_settlement: ClipboardCheck,
  refinance_sweet_spot: RefreshCcwDot,
  fixed_rate_expiry: Percent,
  intro_rate_expiry: Percent,
  io_expiry: CalendarClock,
  loan_maturity: CalendarClock,
  pre_approval_expiry: Hourglass,
  dormant: BellRing,
  follow_up: ListChecks,
  custom: ListChecks,
};

function reasonIcon(eventType: string) {
  return REASON_ICON[eventType] ?? CalendarClock;
}

/** Weekday short for a future due date, Brisbane-anchored ("Today" handled by caller). */
function weekdayShort(ms: number): string {
  return new Date(ms).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'short' });
}

/** Pill text + colour classes from the signed day delta. */
function duePill(row: DueClientEventRow): { text: string; cls: string } {
  if (row.overdue) {
    const d = Math.abs(row.daysFromToday);
    const cls =
      d > 3
        ? 'bg-red-500/15 text-red-300 border-red-500/25'
        : 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    return { text: `${d}d over`, cls };
  }
  if (row.daysFromToday === 0) {
    return { text: 'Today', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' };
  }
  return { text: weekdayShort(row.dueAtMs), cls: 'bg-gray-700/50 text-gray-300 border-gray-600/40' };
}

const CallButtons: React.FC<{ row: DueClientEventRow }> = ({ row }) => {
  const phonePreferred = row.preferredContactMethod === 'phone' || row.preferredContactMethod === 'any' || row.preferredContactMethod == null;
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      {row.phone ? (
        <a
          href={`tel:${row.phone}`}
          title={`Call ${row.phone}`}
          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
            phonePreferred
              ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
              : 'bg-gray-700/40 border border-gray-600/40 text-gray-300 hover:bg-gray-700'
          }`}
        >
          <Phone className="w-3.5 h-3.5" />
        </a>
      ) : (
        <span className="w-8 h-8 rounded-lg bg-gray-700/30 border border-gray-700/40 text-gray-600 flex items-center justify-center opacity-50" title="No phone on file">
          <Phone className="w-3.5 h-3.5" />
        </span>
      )}
      {row.email ? (
        <a
          href={`mailto:${row.email}`}
          title={`Email ${row.email}`}
          className="w-8 h-8 rounded-lg bg-gray-700/40 border border-gray-600/40 text-gray-400 hover:bg-gray-700 hover:text-white flex items-center justify-center transition-colors"
        >
          <Mail className="w-3.5 h-3.5" />
        </a>
      ) : (
        <span className="w-8 h-8 rounded-lg bg-gray-700/30 border border-gray-700/40 text-gray-600 flex items-center justify-center opacity-50" title="No email on file">
          <Mail className="w-3.5 h-3.5" />
        </span>
      )}
    </div>
  );
};

const CallRow: React.FC<{ row: DueClientEventRow; onSelect: (id: string) => void }> = ({ row, onSelect }) => {
  const pill = duePill(row);
  const ReasonIcon = reasonIcon(row.eventType);
  return (
    <div
      onClick={() => onSelect(row.clientId)}
      className="px-5 py-3 flex items-center gap-3.5 hover:bg-gray-700/30 transition-colors cursor-pointer"
    >
      <div className="w-14 flex-shrink-0 text-center">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${pill.cls}`}>
          {pill.text}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white truncate">{row.displayName}</span>
          {row.ambiguousIdentity && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30" title="Possible two people on one record">
              <AlertTriangle className="w-2.5 h-2.5" /> ambiguous
            </span>
          )}
        </div>
        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5 min-w-0">
          <ReasonIcon className="w-3 h-3 text-gray-500 flex-shrink-0" />
          <span className="truncate capitalize">{row.label}</span>
          {row.primaryContactName && (
            <>
              <span className="text-gray-600">&middot;</span>
              <span className="truncate">c/o {row.primaryContactName}</span>
            </>
          )}
        </div>
        {(row.primaryBrokerDisplay || row.officeName) && (
          <div className="text-[11px] text-gray-500 mt-0.5 truncate">
            {row.primaryBrokerDisplay ?? '—'}{row.officeName ? ` · ${row.officeName}` : ''}
          </div>
        )}
      </div>
      <CallButtons row={row} />
    </div>
  );
};

const Column: React.FC<{
  kind: 'overdue' | 'due';
  rows: DueClientEventRow[];
  total: number;
  onSelect: (id: string) => void;
}> = ({ kind, rows, total, onSelect }) => {
  const overdue = kind === 'overdue';
  return (
    <div className={overdue ? 'border-b lg:border-b-0 lg:border-r border-gray-700/50' : ''}>
      {/* Sub-header */}
      <div className={`px-5 py-3 flex items-center justify-between ${overdue ? 'bg-red-500/[0.04]' : 'bg-emerald-500/[0.04]'}`}>
        <div className="flex items-center gap-2.5">
          {overdue ? (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500/60 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
          ) : (
            <span className="inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          )}
          <span className={`text-[11px] font-bold uppercase tracking-wider ${overdue ? 'text-red-400' : 'text-emerald-400'}`}>
            {overdue ? 'Overdue' : 'Due this week'}
          </span>
          <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-md text-[11px] font-bold border ${
            overdue ? 'bg-red-500/15 border-red-500/25 text-red-300' : 'bg-emerald-500/15 border-emerald-500/25 text-emerald-300'
          }`}>
            {total}
          </span>
        </div>
        <span className="text-[10px] text-gray-500 flex items-center gap-1">
          {overdue
            ? <><AlertTriangle className="w-3 h-3 text-red-400/70" /> call now</>
            : <><Calendar className="w-3 h-3 text-emerald-400/70" /> next 7 days</>}
        </span>
      </div>

      {rows.length > 0 ? (
        <>
          <div className="divide-y divide-gray-700/30">
            {rows.map((r) => <CallRow key={r.eventId} row={r} onSelect={onSelect} />)}
          </div>
          {total > rows.length && (
            <div className="px-5 py-2 text-center text-[11px] text-gray-500 border-t border-gray-700/30">
              + {total - rows.length} more
            </div>
          )}
        </>
      ) : (
        <div className="px-5 py-8 flex flex-col items-center justify-center text-center">
          <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-2">
            <Check className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-xs text-gray-400">{overdue ? 'Nothing overdue' : 'Nothing due this week'}</p>
        </div>
      )}
    </div>
  );
};

export const ClientCareWhoToCallPanel: React.FC<ClientCareWhoToCallPanelProps> = ({
  companyId,
  onSelectClient,
  officeId = null,
  brokerDisplay = null,
  loanBookEntityCode = null,
  status = null,
}) => {
  // Collapsed by default — same accordion behaviour as the dashboard Who-To-Call widget.
  const [isOpen, setIsOpen] = useState(false);
  const { overdue, dueThisWeek, counts, loading, error } = useDueClientEvents({
    companyId,
    officeId,
    brokerDisplay,
    loanBookEntityCode,
    status,
  });

  const totalThisWeek = counts.overdue + counts.dueThisWeek;
  const hasOverdue = counts.overdue > 0;

  if (loading) {
    // Collapsed-height skeleton.
    return (
      <div className="bg-gray-800/80 border border-gray-700/60 rounded-xl overflow-hidden animate-pulse">
        <div className="px-5 py-3.5 flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-gray-700" />
          <div className="h-4 w-28 bg-gray-700 rounded" />
          <div className="h-4 w-20 bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/80 border border-gray-700/60 rounded-xl overflow-hidden">
      {/* Collapsed header — the whole strip toggles the accordion (chevron on the right). */}
      <button
        onClick={() => setIsOpen((p) => !p)}
        className="w-full px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-gray-700/30 transition-colors cursor-pointer text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-blue-500/15 border border-blue-500/25 flex items-center justify-center flex-shrink-0">
            <PhoneCall className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold text-white">Who To Call</span>
            <span className="text-[10px] text-gray-500">Clients due for outreach</span>
          </div>

          {totalThisWeek === 0 ? (
            <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-2 py-0.5">
              <Check className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] font-medium text-emerald-400">All caught up</span>
            </div>
          ) : (
            <>
              {/* Overdue count with ping (urgency cue) */}
              {hasOverdue && (
                <>
                  <div className="relative flex-shrink-0">
                    <div className="relative w-2 h-2 rounded-full bg-red-500">
                      <span className="absolute inset-0 rounded-full bg-red-500/50 animate-ping" />
                    </div>
                  </div>
                  <span className="text-xs font-medium text-red-400">{counts.overdue} overdue</span>
                </>
              )}
              <span className="text-gray-600 text-xs hidden sm:inline">&middot;</span>
              <span className="hidden sm:inline text-xs text-emerald-400/90">{counts.dueThisWeek} due this week</span>
            </>
          )}
        </div>

        <ChevronDown
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expandable body — animated via grid-rows so variable height stays smooth. */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          <div className="border-t border-gray-700/40">
            {error ? (
              <div className="px-5 py-3 bg-red-900/20 text-red-300 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            ) : totalThisWeek === 0 ? (
              <div className="px-5 py-8 flex flex-col items-center justify-center text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3">
                  <Check className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-sm font-medium text-gray-300">You're all caught up</p>
                <p className="text-xs text-gray-500 mt-1">No clients due for outreach this week</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <Column kind="overdue" rows={overdue} total={counts.overdue} onSelect={onSelectClient} />
                  <Column kind="due" rows={dueThisWeek} total={counts.dueThisWeek} onSelect={onSelectClient} />
                </div>
                {/* Footer */}
                <div className="px-5 py-2.5 bg-gray-800/50 border-t border-gray-700/40 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-4 text-gray-400">
                    {counts.overdue > 0 && (
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> {counts.overdue} overdue</span>
                    )}
                    {counts.dueThisWeek > 0 && (
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> {counts.dueThisWeek} due this week</span>
                    )}
                  </div>
                  <span className="text-gray-500 hidden sm:flex items-center gap-1.5">
                    <Info className="w-3 h-3" /> Click a client to open their file &amp; log the call
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientCareWhoToCallPanel;
